"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { ArrowRight, Upload, Mic, Square, Play, Check } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";

const DARK_BG = "#0A1F12";
const ACCENT = "#1EB954";

/**
 * The clone says this exact line back to the user.
 * Matches the urgent register the user reads in CALIBRATION_PHRASES so
 * Fish AI has perfect tonal anchor.
 */
const DEFAULT_SCRIPT =
  "Hey mom, it's me, I had an accident and I need your help right now. Please send eight hundred dollars to this Zelle. Hurry, I'm scared.";

/**
 * Calibration phrases — what the user reads aloud (teleprompter, one at a time).
 * Mirrors the urgent register of the synthesis script so Fish AI clones the
 * right emotional tone, not just the voice timbre. User goes from setup → a
 * realistic urgent simulation → back to context.
 */
const CALIBRATION_PHRASES = [
  "Hi, I'm recording a sample so an AI can learn how I speak.",
  "Now imagine I called you in real panic — my voice would sound like this.",
  "Hey mom, it's me, I had an accident and I need your help right now.",
  "Please send eight hundred dollars to this Zelle. Hurry, I'm scared.",
];

const CALIBRATION_SCRIPT = CALIBRATION_PHRASES.join(" ");

const MAX_AUDIO_BYTES = 15 * 1024 * 1024;
const MAX_AUDIO_DURATION_SEC = 60;
const MAX_RECORD_SECONDS = 60;
const MAX_SCRIPT_CHARS = 200;
const ACCEPTED_AUDIO_EXTS = /\.(mp3|wav|m4a|webm|ogg|flac|aac|mp4)$/i;

type Phase = "input" | "loading" | "result" | "error";
type InputMode = "upload" | "record";

const LOADING_MESSAGES = [
  "uploading audio…",
  "analyzing your voice…",
  "cloning with AI…",
  "synthesizing speech…",
  "almost done…",
];

export default function TryClonePage() {
  const [phase, setPhase] = useState<Phase>("input");
  const [mode, setMode] = useState<InputMode>("record");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const [script, setScript] = useState(DEFAULT_SCRIPT);
  const [consent, setConsent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [loadingIdx, setLoadingIdx] = useState(0);

  const [recording, setRecording] = useState(false);
  const [recordedSeconds, setRecordedSeconds] = useState(0);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [audioLevels, setAudioLevels] = useState<number[]>(Array(12).fill(0));
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    trackEvent("try_clone_page_viewed");
  }, []);

  useEffect(() => {
    if (phase !== "loading") return;
    const iv = setInterval(() => {
      setLoadingIdx((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 2500);
    return () => clearInterval(iv);
  }, [phase]);

  useEffect(() => {
    return () => {
      if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, [audioPreviewUrl]);

  const startAudioAnalysis = (stream: MediaStream) => {
    try {
      const ctx = new (
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext
      )();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.6;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const levels = Array.from({ length: 12 }, (_, i) => {
          const idx = Math.floor((i * dataArray.length) / 12);
          return dataArray[idx] / 255;
        });
        setAudioLevels(levels);
        animationRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // analyser failure is non-fatal — recording still works
    }
  };

  const stopAudioAnalysis = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevels(Array(12).fill(0));
  };

  const setAudio = (blob: Blob) => {
    if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
    setAudioBlob(blob);
    setAudioPreviewUrl(URL.createObjectURL(blob));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_AUDIO_BYTES) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      setErrorMsg(
        `File is ${sizeMB}MB. Maximum is 15MB. Try compressing or trimming the audio.`,
      );
      e.target.value = "";
      return;
    }

    if (
      !ACCEPTED_AUDIO_EXTS.test(file.name) &&
      !file.type.startsWith("audio/")
    ) {
      setErrorMsg(
        `File type not supported (${file.type || "unknown"}). Try MP3, WAV, M4A, OGG, or FLAC.`,
      );
      e.target.value = "";
      return;
    }

    let duration = 0;
    try {
      duration = await new Promise<number>((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const a = new Audio();
        const timeout = setTimeout(() => reject(new Error("timeout")), 5000);
        a.addEventListener(
          "loadedmetadata",
          () => {
            clearTimeout(timeout);
            URL.revokeObjectURL(url);
            resolve(a.duration);
          },
          { once: true },
        );
        a.addEventListener(
          "error",
          () => {
            clearTimeout(timeout);
            URL.revokeObjectURL(url);
            reject(new Error("decode failed"));
          },
          { once: true },
        );
        a.src = url;
      });
    } catch {
      setErrorMsg(
        "Could not read this audio file. Try a different format (MP3, WAV, M4A).",
      );
      e.target.value = "";
      return;
    }

    if (duration > MAX_AUDIO_DURATION_SEC) {
      setErrorMsg(
        `Audio is ${Math.round(duration)}s. Maximum is ${MAX_AUDIO_DURATION_SEC}s. Trim before uploading or use the recording option.`,
      );
      e.target.value = "";
      return;
    }

    if (duration < 8) {
      setErrorMsg(
        `Audio is only ${Math.round(duration)}s. Need at least 10 seconds of speech for a clean clone.`,
      );
      e.target.value = "";
      return;
    }

    setErrorMsg("");
    setAudio(file);
    trackEvent("try_clone_audio_uploaded", {
      sizeBytes: file.size,
      durationSec: Math.round(duration),
      mimeType: file.type || "unknown",
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudio(blob);
        stream.getTracks().forEach((t) => t.stop());
        stopAudioAnalysis();
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setRecordedSeconds(0);
      setPhraseIdx(0);
      startAudioAnalysis(stream);
      trackEvent("try_clone_recording_started");
      recordTimerRef.current = setInterval(() => {
        setRecordedSeconds((s) => {
          if (s >= MAX_RECORD_SECONDS - 1) {
            stopRecording();
            return MAX_RECORD_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      setErrorMsg("Microphone access denied. Try uploading a file instead.");
    }
  };

  const advancePhrase = () => {
    if (phraseIdx < CALIBRATION_PHRASES.length - 1) {
      setPhraseIdx((i) => i + 1);
    } else {
      stopRecording();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    setRecording(false);
    trackEvent("try_clone_recording_completed", {
      durationSec: recordedSeconds,
      phrasesCompleted: phraseIdx + 1,
      totalPhrases: CALIBRATION_PHRASES.length,
    });
  };

  const submit = async () => {
    if (!audioBlob) {
      setErrorMsg("Please upload or record audio first.");
      return;
    }
    if (!consent) {
      setErrorMsg("Please confirm voice ownership.");
      return;
    }
    if (!script.trim()) {
      setErrorMsg("Script cannot be empty.");
      return;
    }

    setErrorMsg("");
    setPhase("loading");
    setLoadingIdx(0);

    const startedAt = Date.now();
    trackEvent("try_clone_submitted", {
      mode,
      audioSizeBytes: audioBlob.size,
      scriptLength: script.trim().length,
    });

    try {
      const fd = new FormData();
      const ext = audioBlob.type.includes("webm") ? "webm" : "wav";
      fd.append("audio", audioBlob, `voice.${ext}`);
      fd.append("script", script.trim());
      fd.append("tone", "urgent");
      fd.append("consent", "true");

      const res = await fetch(`${API_URL}/try-clone`, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) {
        if (res.status === 429) {
          throw new Error(
            "Free demo limit reached (20 per hour). Try again in an hour or run a real simulation on your parent.",
          );
        }
        const err = await res
          .json()
          .catch(() => ({ message: `Server returned ${res.status}` }));
        throw new Error(err.message || `Server returned ${res.status}`);
      }

      const data = (await res.json()) as { audioUrl: string };
      setResultUrl(data.audioUrl);
      setPhase("result");
      trackEvent("try_clone_succeeded", {
        mode,
        latencyMs: Date.now() - startedAt,
      });
    } catch (err) {
      const message = (err as Error).message;
      setErrorMsg(message);
      setPhase("error");
      trackEvent("try_clone_failed", {
        mode,
        errorMessage: message.slice(0, 200),
        latencyMs: Date.now() - startedAt,
      });
    }
  };

  const reset = () => {
    if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
    setAudioBlob(null);
    setAudioPreviewUrl(null);
    setResultUrl(null);
    setRecordedSeconds(0);
    setConsent(false);
    setErrorMsg("");
    setPhase("input");
    trackEvent("try_clone_reset", { fromPhase: phase });
  };

  const avgLevel = audioLevels.reduce((a, b) => a + b, 0) / audioLevels.length;

  return (
    <main
      style={{
        minHeight: "100vh",
        background: DARK_BG,
        color: "#F5F5F4",
        padding: "60px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Aurora background — pulses with audio when recording */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: `translate(-50%, -40%) scale(${
              recording ? 1 + avgLevel * 0.4 : 1
            })`,
            width: "120vw",
            height: "160vh",
            borderRadius: "50%",
            background:
              "radial-gradient(circle at center, rgba(30,185,84,0.45) 0%, rgba(30,185,84,0.25) 15%, rgba(30,185,84,0.10) 35%, rgba(30,185,84,0.04) 55%, transparent 70%)",
            filter: "blur(40px)",
            transition: "transform 0.1s ease-out",
            animation: recording
              ? "none"
              : "try-aurora-idle 6s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "70vw",
            height: "70vh",
            borderRadius: "50%",
            border: "2px solid rgba(30,185,84,0.10)",
            animation: "try-ring-expand 4s ease-out infinite",
          }}
        />
      </div>

      <style>{`
        @keyframes try-aurora-idle {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        @keyframes try-ring-expand {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.5; }
          100% { transform: translate(-50%, -50%) scale(1.8); opacity: 0; }
        }
      `}</style>

      <div
        style={{
          maxWidth: 640,
          width: "100%",
          position: "relative",
          zIndex: 2,
        }}
      >
        <Link
          href="/"
          style={{
            color: "rgba(255,255,255,0.5)",
            textDecoration: "none",
            fontSize: 14,
            marginBottom: 32,
            display: "inline-block",
          }}
        >
          ← back to TrustboxAI
        </Link>

        <h1
          style={{
            fontSize: "clamp(32px, 5vw, 44px)",
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: "-1px",
            marginBottom: 12,
          }}
        >
          Hear your voice cloned in 15 seconds.
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "#C4C0BB",
            lineHeight: 1.6,
            marginBottom: 32,
          }}
        >
          No signup. No email. Read the passage below out loud, hear your AI
          clone, feel the gap. Audio deletes in 1 hour.
        </p>

        {phase === "input" && (
          <div
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 20,
              padding: 28,
            }}
          >
            {/* Step 1: Mode toggle (only shown before recording starts) */}
            {!recording && !audioPreviewUrl && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 20,
                  background: "rgba(0,0,0,0.25)",
                  borderRadius: 12,
                  padding: 4,
                }}
              >
                <button
                  onClick={() => setMode("record")}
                  style={tabStyle(mode === "record")}
                >
                  <Mic size={16} /> Read aloud (recommended)
                </button>
                <button
                  onClick={() => setMode("upload")}
                  style={tabStyle(mode === "upload")}
                >
                  <Upload size={16} /> Upload file
                </button>
              </div>
            )}

            {/* RECORD MODE — teleprompter UX */}
            {mode === "record" && (
              <div style={{ marginBottom: 24 }}>
                {/* Idle state — instruction + start */}
                {!recording && !audioPreviewUrl && (
                  <>
                    <div
                      style={{
                        marginBottom: 16,
                        padding: 16,
                        background: "rgba(0,0,0,0.2)",
                        borderRadius: 10,
                      }}
                    >
                      <p
                        style={{
                          fontSize: 13,
                          color: "#A8A29E",
                          lineHeight: 1.6,
                        }}
                      >
                        Click{" "}
                        <strong style={{ color: "#F5F5F4" }}>Start</strong> and
                        read each phrase as it appears. Tap{" "}
                        <strong style={{ color: "#F5F5F4" }}>Next</strong> after
                        each one. Takes about 30 seconds.
                      </p>
                    </div>
                    <button onClick={startRecording} style={recordButton}>
                      <Mic size={20} />
                      Start reading
                    </button>
                  </>
                )}

                {/* Recording state — teleprompter + live audio viz */}
                {recording && (
                  <div>
                    {/* Progress dots */}
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginBottom: 20,
                        justifyContent: "center",
                      }}
                    >
                      {CALIBRATION_PHRASES.map((_, i) => (
                        <div
                          key={i}
                          style={{
                            width: i === phraseIdx ? 28 : 8,
                            height: 8,
                            borderRadius: 4,
                            background:
                              i < phraseIdx
                                ? ACCENT
                                : i === phraseIdx
                                  ? "#86EFAC"
                                  : "rgba(255,255,255,0.15)",
                            boxShadow:
                              i === phraseIdx
                                ? `0 0 12px rgba(134,239,172,${0.4 + avgLevel * 0.6})`
                                : "none",
                            transition: "all 0.3s",
                          }}
                        />
                      ))}
                    </div>

                    {/* Big phrase card with live glow */}
                    <div
                      style={{
                        minHeight: 160,
                        padding: "36px 24px",
                        background: `radial-gradient(circle at center, rgba(30,185,84,${0.08 + avgLevel * 0.18}) 0%, rgba(30,185,84,0.03) 100%)`,
                        border: `2px solid rgba(30,185,84,${0.35 + avgLevel * 0.5})`,
                        borderRadius: 18,
                        marginBottom: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        boxShadow: `0 0 ${20 + avgLevel * 60}px rgba(30,185,84,${0.15 + avgLevel * 0.45})`,
                        transition: "all 0.08s ease-out",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <p
                        key={phraseIdx}
                        style={{
                          fontSize: "clamp(22px, 4.5vw, 30px)",
                          fontWeight: 600,
                          color: "#F5F5F4",
                          lineHeight: 1.35,
                          letterSpacing: "-0.5px",
                          animation: "phrase-fade-in 0.4s ease-out",
                          textWrap: "balance",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        &ldquo;{CALIBRATION_PHRASES[phraseIdx]}&rdquo;
                      </p>
                    </div>

                    {/* Live waveform — 12 bars react to mic input */}
                    <div
                      style={{
                        display: "flex",
                        gap: 4,
                        alignItems: "center",
                        justifyContent: "center",
                        height: 40,
                        marginBottom: 16,
                      }}
                    >
                      {audioLevels.map((level, i) => (
                        <div
                          key={i}
                          style={{
                            width: 4,
                            height: `${Math.max(6, level * 40)}px`,
                            background: ACCENT,
                            borderRadius: 2,
                            opacity: 0.4 + level * 0.6,
                            transition: "height 0.05s ease-out, opacity 0.05s",
                            boxShadow:
                              level > 0.3 ? `0 0 6px ${ACCENT}` : "none",
                          }}
                        />
                      ))}
                    </div>

                    {/* Recording indicator + Next button */}
                    <div style={{ display: "flex", gap: 10 }}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "12px 16px",
                          background: "rgba(220,38,38,0.15)",
                          border: "1px solid rgba(220,38,38,0.3)",
                          borderRadius: 10,
                          color: "#FCA5A5",
                          fontSize: 14,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "#DC2626",
                            animation: "rec-pulse 1s ease-in-out infinite",
                          }}
                        />
                        REC {recordedSeconds}s
                      </div>
                      <button
                        onClick={advancePhrase}
                        style={{ ...recordButton, flex: 1 }}
                      >
                        {phraseIdx < CALIBRATION_PHRASES.length - 1 ? (
                          <>
                            Next phrase ({phraseIdx + 1}/
                            {CALIBRATION_PHRASES.length}) →
                          </>
                        ) : (
                          <>
                            <Square size={18} /> Done — submit
                          </>
                        )}
                      </button>
                    </div>
                    <style>{`
                      @keyframes phrase-fade-in {
                        from { opacity: 0; transform: translateY(12px); filter: blur(4px); }
                        to { opacity: 1; transform: translateY(0); filter: blur(0); }
                      }
                      @keyframes rec-pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.3; }
                      }
                    `}</style>
                  </div>
                )}

                {/* Recorded preview */}
                {!recording && audioPreviewUrl && (
                  <div>
                    <p
                      style={{
                        fontSize: 13,
                        color: "#86EFAC",
                        marginBottom: 8,
                        fontWeight: 600,
                      }}
                    >
                      ✓ Recorded {recordedSeconds}s — preview below
                    </p>
                    <audio
                      src={audioPreviewUrl}
                      controls
                      style={{ width: "100%", marginBottom: 12 }}
                    />
                    <button
                      onClick={() => {
                        setAudioBlob(null);
                        setAudioPreviewUrl(null);
                        setRecordedSeconds(0);
                        setPhraseIdx(0);
                      }}
                      style={ghostButton}
                    >
                      Re-record
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* UPLOAD MODE */}
            {mode === "upload" && !recording && (
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{
                    marginBottom: 12,
                    padding: 14,
                    background: "rgba(0,0,0,0.2)",
                    borderRadius: 10,
                    fontSize: 13,
                    color: "#A8A29E",
                    lineHeight: 1.5,
                  }}
                >
                  <strong style={{ color: "#F5F5F4" }}>
                    For best results:
                  </strong>{" "}
                  upload audio of yourself reading: &ldquo;{CALIBRATION_SCRIPT}
                  &rdquo;
                </div>
                <label
                  htmlFor="audio-upload"
                  style={{
                    display: "block",
                    border: "2px dashed rgba(30,185,84,0.4)",
                    borderRadius: 12,
                    padding: 32,
                    textAlign: "center",
                    cursor: "pointer",
                    background: "rgba(30,185,84,0.05)",
                  }}
                >
                  <Upload
                    size={32}
                    style={{ color: ACCENT, marginBottom: 8 }}
                  />
                  <p style={{ fontSize: 14, color: "#D4D0CC" }}>
                    Click to upload mp3, wav, m4a, ogg, or flac
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      color: "rgba(255,255,255,0.5)",
                      marginTop: 4,
                    }}
                  >
                    max 15MB · 10-60 seconds
                  </p>
                  <input
                    id="audio-upload"
                    type="file"
                    accept="audio/*"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                </label>
                {audioPreviewUrl && (
                  <audio
                    src={audioPreviewUrl}
                    controls
                    style={{ width: "100%", marginTop: 12 }}
                  />
                )}
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  color: "#86EFAC",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                What your clone will say (editable)
              </label>
              <textarea
                value={script}
                onChange={(e) =>
                  setScript(e.target.value.slice(0, MAX_SCRIPT_CHARS))
                }
                rows={3}
                style={{
                  width: "100%",
                  background: "rgba(0,0,0,0.3)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 10,
                  padding: 12,
                  color: "#F5F5F4",
                  fontSize: 15,
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
              />
              <p
                style={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.4)",
                  marginTop: 4,
                  textAlign: "right",
                }}
              >
                {script.length} / {MAX_SCRIPT_CHARS}
              </p>
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginBottom: 20,
                cursor: "pointer",
                fontSize: 14,
                color: "#D4D0CC",
                lineHeight: 1.5,
              }}
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ marginTop: 4, accentColor: ACCENT }}
              />
              I confirm this is my own voice or I have permission to use it.
            </label>

            {errorMsg && (
              <p
                style={{
                  color: "#FCA5A5",
                  fontSize: 14,
                  marginBottom: 16,
                  background: "rgba(220,38,38,0.1)",
                  padding: 12,
                  borderRadius: 8,
                }}
              >
                {errorMsg}
              </p>
            )}

            <button
              onClick={submit}
              disabled={!audioBlob || !consent}
              style={{
                ...primaryButton,
                opacity: !audioBlob || !consent ? 0.5 : 1,
                cursor: !audioBlob || !consent ? "not-allowed" : "pointer",
              }}
            >
              Hear my clone <ArrowRight size={18} />
            </button>
          </div>
        )}

        {phase === "loading" && (
          <div
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(30,185,84,0.2)",
              borderRadius: 20,
              padding: 60,
              textAlign: "center",
              boxShadow: "0 0 60px rgba(30,185,84,0.15)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                justifyContent: "center",
                height: 80,
                marginBottom: 28,
              }}
            >
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 6,
                    height: 60,
                    background: ACCENT,
                    borderRadius: 3,
                    transformOrigin: "center",
                    animation: `loading-wave 1.2s ease-in-out ${i * 0.12}s infinite`,
                    boxShadow: `0 0 10px ${ACCENT}`,
                  }}
                />
              ))}
            </div>
            <p style={{ color: "#86EFAC", fontSize: 17, fontWeight: 500 }}>
              {LOADING_MESSAGES[loadingIdx]}
            </p>
            <p
              style={{
                color: "rgba(255,255,255,0.4)",
                fontSize: 13,
                marginTop: 10,
              }}
            >
              this takes 15-30 seconds
            </p>
            <style>{`
              @keyframes loading-wave {
                0%, 100% { transform: scaleY(0.2); opacity: 0.4; }
                50% { transform: scaleY(1); opacity: 1; }
              }
            `}</style>
          </div>
        )}

        {phase === "result" && resultUrl && (
          <div
            style={{
              background: "rgba(30,185,84,0.06)",
              border: "1px solid rgba(30,185,84,0.25)",
              borderRadius: 20,
              padding: 32,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(30,185,84,0.15)",
                color: "#86EFAC",
                padding: "4px 12px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                marginBottom: 16,
              }}
            >
              <Check size={12} /> Your clone
            </div>
            <h2
              style={{
                fontSize: 26,
                fontWeight: 700,
                marginBottom: 12,
                lineHeight: 1.2,
              }}
            >
              That was your voice.
            </h2>
            <p style={{ fontSize: 16, color: "#D4D0CC", marginBottom: 20 }}>
              Your mom would not catch this in a panic. That is the point.
            </p>
            <audio
              src={resultUrl}
              controls
              autoPlay
              style={{ width: "100%", marginBottom: 24 }}
            />
            <Link
              href="/dashboard/simulate"
              onClick={() => {
                trackEvent("try_clone_cta_clicked", {
                  ctaLabel: "run_real_simulation",
                  destination: "/dashboard/simulate",
                });
                notifyCtaClick("run_real_simulation", "/dashboard/simulate");
              }}
              style={{ ...primaryButton, textDecoration: "none" }}
            >
              Run a real simulation on your parent — $4.90{" "}
              <ArrowRight size={18} />
            </Link>
            <button
              onClick={reset}
              style={{ ...ghostButton, marginTop: 12, width: "100%" }}
            >
              Try with another voice
            </button>
          </div>
        )}

        {phase === "error" && (
          <div
            style={{
              background: "rgba(220,38,38,0.08)",
              border: "1px solid rgba(220,38,38,0.3)",
              borderRadius: 20,
              padding: 32,
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: 16, marginBottom: 16, color: "#FCA5A5" }}>
              {errorMsg}
            </p>
            <button onClick={reset} style={primaryButton}>
              Try again
            </button>
          </div>
        )}

        <p
          style={{
            fontSize: 12,
            color: "rgba(255,255,255,0.35)",
            textAlign: "center",
            marginTop: 24,
            lineHeight: 1.6,
          }}
        >
          Free demo: 3 clones per hour per IP. Voice deletes within 1 hour.
          <br />
          For paid simulations on family members, see{" "}
          <Link
            href="/dashboard/simulate"
            onClick={() => {
              trackEvent("try_clone_cta_clicked", {
                ctaLabel: "footer_run_simulation",
                destination: "/dashboard/simulate",
              });
              notifyCtaClick("footer_run_simulation", "/dashboard/simulate");
            }}
            style={{ color: "#86EFAC", textDecoration: "underline" }}
          >
            run a real simulation
          </Link>
          .
        </p>
      </div>
    </main>
  );
}

/**
 * Fire-and-forget POST to backend so the operator gets a Telegram ping
 * when an anonymous /try visitor clicks one of the CTAs that lead to the
 * paid simulation flow. This is the highest-intent free → paid signal.
 *
 * Uses sendBeacon when available so navigation doesn't cancel the request;
 * falls back to fetch with keepalive. Never throws — tracking failures
 * must not block the user's navigation.
 */
function notifyCtaClick(ctaLabel: string, destination: string): void {
  try {
    const url = `${API_URL}/try-clone/track-cta`;
    const payload = JSON.stringify({ ctaLabel, destination });

    if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }

    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* tracking must never block UX */
    });
  } catch {
    /* tracking must never block UX */
  }
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  flex: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  padding: "10px 14px",
  border: "none",
  background: active ? ACCENT : "transparent",
  color: active ? "#003822" : "#D4D0CC",
  fontWeight: 600,
  fontSize: 14,
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.2s",
});

const recordButton: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  width: "100%",
  padding: "16px 24px",
  borderRadius: 12,
  background: ACCENT,
  color: "#003822",
  border: "none",
  fontSize: 16,
  fontWeight: 700,
  cursor: "pointer",
};

const primaryButton: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
  padding: "16px 24px",
  borderRadius: 999,
  background: "#004322",
  color: "white",
  border: "none",
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
};

const ghostButton: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 16px",
  borderRadius: 8,
  background: "transparent",
  color: "#D4D0CC",
  border: "1px solid rgba(255,255,255,0.15)",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};
